/** Shared text-only tooltips for HUD glyphs. No HTML from game or player text is interpreted. */
let active: HTMLElement | null = null;
let tooltip: HTMLElement | null = null;
let tooltipRoot: HTMLElement | null = null;
/** The parchment inside the tooltip, which holds the text; the outer node only carries its cast shadow. */
let paper: HTMLElement | null = null;
let cleanup: (() => void) | undefined;

/** The tooltip is a manual popover, so it sits in the top layer above an open modal and escapes the modal's clipping; reshowing lifts it over a modal opened since. */
function setShown(node: HTMLElement, shown: boolean) {
  if (!shown) { node.hidden = true; if (node.matches(':popover-open')) node.hidePopover(); return; }
  if (node.hidden) node.hidden = false;
  if (node.matches(':popover-open')) node.hidePopover();
  node.showPopover();
}

export function setTooltip(target: HTMLElement, text: string, label?: string) {
  if (target.dataset.tooltip !== text) target.dataset.tooltip = text;
  target.removeAttribute('title');
  if (label !== undefined) target.setAttribute('aria-label', label);
  if (target === active && paper && paper.textContent !== text) paper.textContent = text;
}

/**
 * Shows a tooltip for something that is not an element (a piece of the 3D HUD), above a point in client pixels; null text hides it.
 * Any pointer press or element tooltip replaces it.
 */
export function pointTooltip(text: string | null, x = 0, y = 0) {
  if (!tooltip || !paper || !tooltipRoot) return;
  if (!text) { if (!active) setShown(tooltip, false); return; }
  active = null;
  if (paper.textContent !== text) paper.textContent = text;
  setShown(tooltip, true);
  const bounds = tooltip.getBoundingClientRect(), edge = 10;
  tooltip.style.left = `${Math.max(edge, Math.min(window.innerWidth - bounds.width - edge, x - bounds.width / 2))}px`;
  const above = y - bounds.height - 18;
  tooltip.style.top = `${Math.max(edge, Math.min(window.innerHeight - bounds.height - edge, above >= edge ? above : y + 24))}px`;
}

export function mountTooltips(root: HTMLElement) {
  cleanup?.();
  const node = document.createElement('div'); node.id = 'hud-tooltip'; node.className = 'hud-tooltip'; node.setAttribute('role', 'tooltip'); node.popover = 'manual'; node.hidden = true;
  const sheet = document.createElement('div'); sheet.className = 'hud-tooltip-paper'; node.append(sheet);
  root.append(node); tooltip = node; paper = sheet; tooltipRoot = root;
  const events = new AbortController(), options = { signal: events.signal };
  const find = (target: EventTarget | null) => target instanceof Element ? target.closest<HTMLElement>('[data-tooltip]') : null;
  const hide = () => { active = null; setShown(node, false); };
  const position = () => {
    if (!active?.isConnected || active.closest('[hidden]')) { hide(); return; }
    const anchor = active.getBoundingClientRect(), bounds = node.getBoundingClientRect(), edge = 10;
    node.style.left = `${Math.max(edge, Math.min(window.innerWidth - bounds.width - edge, anchor.left + anchor.width / 2 - bounds.width / 2))}px`;
    const above = anchor.top - bounds.height - 10;
    node.style.top = `${Math.max(edge, Math.min(window.innerHeight - bounds.height - edge, above >= edge ? above : anchor.bottom + 10))}px`;
  };
  const show = (target: HTMLElement | null) => {
    if (!target?.dataset.tooltip) { hide(); return; }
    if (active !== target) hide();
    active = target;
    if (sheet.textContent !== target.dataset.tooltip) sheet.textContent = target.dataset.tooltip;
    if (node.hidden || !node.matches(':popover-open')) setShown(node, true);
    position();
  };
  let hovered: HTMLElement | null = null;
  // Only keyboard focus keeps a tooltip alive; a clicked control stays focused but must not resurrect its tooltip.
  const focused = () => { const el = find(document.activeElement); return el?.matches(':focus-visible') ? el : null; };
  let pointer = { x: -1, y: -1 };
  // Controls can vanish, disable or hide under a still pointer without pointerout; drop tooltips whose anchor no longer sits under it.
  const watchdog = window.setInterval(() => {
    if (!active) return;
    const rect = active.getBoundingClientRect();
    const gone = !active.isConnected || rect.width === 0 || rect.height === 0 || active.closest('[hidden]') !== null;
    const under = hovered === active && (pointer.x < rect.left - 2 || pointer.x > rect.right + 2 || pointer.y < rect.top - 2 || pointer.y > rect.bottom + 2);
    if (gone || under || (hovered !== active && focused() !== active)) { hovered = null; hide(); }
  }, 200);
  root.addEventListener('pointerover', event => { hovered = find(event.target); show(hovered ?? focused()); }, options);
  root.addEventListener('pointerout', event => { hovered = find(event.relatedTarget); show(hovered ?? focused()); }, options);
  // Cursor capture forwards pointermove to the control under the virtual cursor, without pointerover/out.
  root.addEventListener('pointermove', event => {
    pointer = { x: event.clientX, y: event.clientY };
    const target = find(event.target);
    if (target !== hovered) { hovered = target; show(hovered ?? focused()); }
  }, options);
  document.addEventListener('pointerleave', () => { hovered = null; hide(); }, options);
  window.addEventListener('blur', hide, options);
  root.addEventListener('focusin', event => show(hovered ?? focused()), options);
  root.addEventListener('focusout', event => show(hovered ?? (find(event.relatedTarget)?.matches(':focus-visible') ? find(event.relatedTarget) : null)), options);
  root.addEventListener('click', hide, options);
  root.addEventListener('pointerdown', hide, options);
  root.addEventListener('dragstart', hide, options);
  document.addEventListener('keydown', event => { if (event.key === 'Escape') hide(); }, options);
  window.addEventListener('resize', hide, options);
  document.addEventListener('scroll', hide, { ...options, capture: true });
  const observer = new MutationObserver(records => {
    if (!active) return;
    // Our own visibility writes must not trigger another layout measurement.
    // A text change still needs one measurement so a wider hint stays on screen.
    if (records.some(record => record.target !== node && !node.contains(record.target)
      || record.type === 'childList' && record.target === node && !node.hidden)) position();
  });
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'data-tooltip'] });
  cleanup = () => { window.clearInterval(watchdog); hide(); events.abort(); observer.disconnect(); node.remove(); tooltip = null; paper = null; tooltipRoot = null; };
}
